import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CloseIcon from '@mui/icons-material/Close';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import { Link } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';

const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [state, setState] = useState({ loading: false, loaded: false, courses: [], lessons: [], instructors: [] });

  useEffect(() => {
    if (!open || state.loaded) return undefined;
    let active = true;
    setState((current) => ({ ...current, loading: true }));
    Promise.all([
      contentApi.listCourses({ status: { eq: 'PUBLISHED' } }),
      contentApi.listLessons({ status: { eq: 'PUBLISHED' } }),
      contentApi.listInstructors({ status: { eq: 'PUBLISHED' } }),
    ]).then(([courses, lessons, instructors]) => {
      if (active) setState({ loading: false, loaded: true, courses, lessons, instructors });
    }).catch((error) => {
      console.info('Global search is not available yet.', error);
      if (active) setState({ loading: false, loaded: true, courses: [], lessons: [], instructors: [] });
    });
    return () => { active = false; };
  }, [open, state.loaded]);

  const results = useMemo(() => {
    const words = normalize(query).split(' ').filter(Boolean);
    if (!words.length) return [];
    const matches = (text) => words.every((word) => normalize(text).includes(word));
    return [
      ...state.courses.filter((item) => matches([item.title, item.shortDescription, item.description, ...(item.tags || [])].join(' '))).map((item) => ({ id: `course-${item.id}`, type: 'Course', title: item.title, subtitle: item.difficulty?.replaceAll('_', ' '), to: `/courses/${item.slug}`, icon: AutoStoriesIcon })),
      ...state.lessons.filter((item) => matches([item.title, item.description].join(' '))).map((item) => ({ id: `lesson-${item.id}`, type: 'Lesson', title: item.title, subtitle: item.durationMinutes ? `${item.durationMinutes} minute class` : 'Dance class', to: `/lessons/${item.slug}`, icon: PlayCircleOutlineIcon })),
      ...state.instructors.filter((item) => matches([item.name, item.bio, item.country, ...(item.styles || [])].join(' '))).map((item) => ({ id: `instructor-${item.id}`, type: 'Instructor', title: item.name, subtitle: item.country || 'JA Legends instructor', to: `/instructors/${item.slug}`, icon: PersonSearchIcon })),
    ].slice(0, 14);
  }, [query, state]);

  const close = () => { setOpen(false); setQuery(''); };

  return (
    <>
      <Tooltip title="Search lessons, courses, and instructors" arrow>
        <IconButton className="dance-nav-search" aria-label="Search JA Legends" onClick={() => setOpen(true)}><SearchIcon /></IconButton>
      </Tooltip>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm" PaperProps={{ className: 'global-search-dialog' }}>
        <DialogContent>
          <Box className="global-search-heading">
            <Box><Typography variant="overline">Find your next move</Typography><Typography variant="h4" fontWeight={950}>Search JA Legends</Typography></Box>
            <IconButton aria-label="Close search" onClick={close}><CloseIcon /></IconButton>
          </Box>
          <TextField
            autoFocus
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search a step, song, teacher, or course…"
            inputProps={{ 'aria-label': 'Search lessons, courses, and instructors' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <Box className="global-search-results">
            {state.loading ? <CircularProgress size={30} /> : !query.trim() ? (
              <Typography color="text.secondary">Try “beginner,” an instructor’s name, or a Dancehall step.</Typography>
            ) : !results.length ? (
              <Typography color="text.secondary">No results found. Try a different word.</Typography>
            ) : (
              <List disablePadding>
                {results.map((result) => {
                  const Icon = result.icon;
                  return <ListItemButton component={Link} to={result.to} onClick={close} key={result.id}><ListItemIcon><Icon /></ListItemIcon><ListItemText primary={result.title} secondary={`${result.type}${result.subtitle ? ` · ${result.subtitle}` : ''}`} /></ListItemButton>;
                })}
              </List>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
